import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';

import CustomerModal from './CustomerAddEditModal';
import { useNavigate } from "react-router-dom";
import { useUser } from '../../context/UserContext';
import { Link } from 'react-router-dom';
import axiosInstance from '../../services/api/axios';
import { toast } from 'react-toastify';
import ConfirmationModal from '../../components/ConfirmationModal';
import moment from 'moment';
import { Eye, Plus } from 'lucide-react';
import { IconButton, Switch } from '@material-tailwind/react';
import usePagination from '../../hooks/usePagination';
import PaginationControls from '../../components/PaginationControls';
import SearchInput from '../../components/SearchInput';
import DataTable from '../../components/DataTable';

// Cell Renderer Components
const SUBSCRIPTION_TAG_TONES = {
    BASIC: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    STANDARD: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    TRIAL_READ_ONLY: 'bg-amber-50 text-amber-700 border-amber-200',
    ENTERPRISE: 'bg-flamingo-50 text-flamingo-700 border-flamingo-200',
    TRIAL: 'bg-rose-50 text-rose-700 border-rose-200',
};

const SubscriptionTagRenderer = ({ value }) => {
    const cls = SUBSCRIPTION_TAG_TONES[value] || 'bg-slate-100 text-slate-700 border-slate-200';
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold border ${cls}`}>
            {value || 'N/A'}
        </span>
    );
};

const computeTrialStatus = (endDate) => {
    if (!endDate) return 'N/A';
    const trialEnd = moment(endDate);
    const daysLeft = trialEnd.diff(moment(), 'days');
    if (trialEnd.isBefore(moment())) return 'Expired';
    if (daysLeft <= 7) return 'Expiring Soon';
    return 'Active';
};

const TrialStatusRenderer = ({ value }) => {
    const status = computeTrialStatus(value);
    const cls =
        status === 'Expired'
            ? 'bg-rose-50 text-rose-700 border-rose-200'
            : status === 'Expiring Soon'
            ? 'bg-amber-50 text-amber-700 border-amber-200'
            : status === 'Active'
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'bg-slate-100 text-slate-600 border-slate-200';
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold border ${cls}`}>
            {status}
        </span>
    );
};

const StatusRenderer = ({ value }) => (
    <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${
            value
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-rose-50 text-rose-700 border-rose-200'
        }`}
    >
        <span className={`w-1.5 h-1.5 rounded-full ${value ? 'bg-emerald-500' : 'bg-rose-500'}`} />
        {value ? 'ACTIVE' : 'INACTIVE'}
    </span>
);

const ActionsRenderer = ({ data, onActivateDeactivate, isLoading }) => (
    <div className="flex items-center justify-center gap-2">
        <Link to={`/customers/view/${data.customer.customerId}`}>
            <button
                type="button"
                className="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-500 hover:text-flamingo-600 hover:bg-flamingo-50 transition-colors"
                title="View"
                aria-label="View"
            >
                <Eye size={14} />
            </button>
        </Link>
        <Switch
            checked={data?.customer?.isActive || false}
            color="success"
            onChange={() => {
                if (onActivateDeactivate) {
                    onActivateDeactivate(data.customer.customerId, data.customer.isActive);
                }
            }}
            disabled={isLoading}
        />
    </div>
);

const CustomerList = ({ filterStatus, onEdit }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [customerToEdit, setcustomerToEdit] = useState(null);
    const navigate = useNavigate();
    const gridRef = useRef(null);

    // Confirmation Modal State
    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
    const [customerToAction, setCustomerToAction] = useState(null);
    const [actionType, setActionType] = useState(null); // 'activate' or 'deactivate'
    const [searchQuery, setSearchQuery] = useState(''); // Search term state
    const [subscriptionFilter, setSubscriptionFilter] = useState('all'); // Subscription filter
    const [trialStatusFilter, setTrialStatusFilter] = useState('all'); // Trial status filter

    const handleEdit = (customer) => {
        setIsModalOpen(true);
        setcustomerToEdit(customer);
    };

    const handleSubmit = (customerData) => {
        setIsModalOpen(false);
    };

    // Create fetch function for customers with pagination
    const fetchCustomersBackend = useCallback(async ({ page, page_size, search }) => {
        try {
            const params = { 
                page, 
                page_size,
                customerType: "DEVELOPER"
            };
            
            if (search) {
                params.search = search;
            }

            const response = await axiosInstance.get('/customers/subscription', { params });
            
            // Handle both paginated and non-paginated responses
            if (response?.items && response.total !== undefined) {
                // Already paginated format
                return response;
            } else if (Array.isArray(response)) {
                // Fallback for non-paginated response
                const startIndex = (page - 1) * page_size;
                const endIndex = startIndex + page_size;
                const paginatedItems = response.slice(startIndex, endIndex);
                return {
                    items: paginatedItems,
                    total: response.length,
                    page,
                    page_size,
                    total_pages: Math.ceil(response.length / page_size),
                    has_next: endIndex < response.length,
                    has_previous: page > 1
                };
            } else {
                return { items: [], total: 0, page, page_size, total_pages: 0, has_next: false, has_previous: false };
            }
        } catch (error) {
            console.error('Error fetching customers:', error);
            toast.error("Failed to fetch customers.", { position: "top-right" });
            return { items: [], total: 0, page: 1, page_size: 30, total_pages: 0, has_next: false, has_previous: false };
        }
    }, []);

    // Automatic (debounced) search — auto mode fetches when the committed search changes.
    const {
        data: customers,
        pagination,
        loading: isLoading,
        error: paginationError,
        goToPage,
        changePageSize,
        changeSearch,
        refetch
    } = usePagination(fetchCustomersBackend, 30, '', true);

    // Keep the latest changeSearch without re-running the debounce on every render.
    const changeSearchRef = useRef(changeSearch);
    changeSearchRef.current = changeSearch;

    // Debounce: commit the typed query 400ms after the user stops typing.
    useEffect(() => {
        const timer = setTimeout(() => changeSearchRef.current(searchQuery), 400);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Note: filterStatus, subscriptionFilter, and trialStatusFilter are applied client-side
    // so we don't need to refetch when they change

    // Function to handle activation/deactivation confirmation
    const handleActivateDeactivateConfirmation = (customerId, currentStatus) => {
        setCustomerToAction(customerId);
        setActionType(currentStatus ? 'deactivate' : 'activate');
        setConfirmationModalOpen(true);
    };

    // Function to execute the activation/deactivation after confirmation
    const handleConfirmActivateDeactivate = async () => {
        setConfirmationModalOpen(false);

        try {
            const newStatus = actionType === 'activate';
            const formData = new FormData();
            formData.append("is_active", newStatus); // Only `is_active` in the body

            await axiosInstance.put(`/customers`, formData, {
                params: { customerId: customerToAction }, // `customerId` in query params
                headers: { "Content-Type": "multipart/form-data" }
            });

            refetch(); // Refresh customer list using pagination hook

            toast.success(`Customer ${newStatus ? 'activated' : 'deactivated'} successfully!`, { position: "top-right" });
        } catch (error) {
            console.error('Error updating customer status:', error);
            toast.error("Failed to update customer status.", { position: "top-right" });
        } finally {
            setCustomerToAction(null);
            setActionType(null);
        }
    };

    const handleCancelActivateDeactivate = () => {
        setConfirmationModalOpen(false);
        setCustomerToAction(null);
        setActionType(null);
    };

    // New Functions to handle Switch changes
    const handleActivateClick = async (customer) => {
        await handleActivateDeactivate(customer.customerId, true);
    };

    const handleDeactivateClick = async (customer) => {
        await handleActivateDeactivate(customer.customerId, false);
    };

    const handleActivateDeactivate = async (customerId, newStatus) => {
        try {
            const formData = new FormData();
            formData.append("is_active", newStatus);

            await axiosInstance.put(`/customers`, formData, {
                params: { customerId: customerId },
                headers: { "Content-Type": "multipart/form-data" }
            });

            refetch(); // Refresh customer list using pagination hook

            toast.success(`Customer ${newStatus ? 'activated' : 'deactivated'} successfully!`, { position: "top-right" });
        } catch (error) {
            console.error('Error updating customer status:', error);
            toast.error("Failed to update customer status.", { position: "top-right" });
        }
    };


    // Helper function for trial status (used in filtering)
    const getTrialExpirationStatus = (endDate) => {
        if (!endDate) {
            return "N/A"; 
        }

        const now = moment();
        const trialEnd = moment(endDate);
        const daysLeft = trialEnd.diff(now, 'days');

        if (trialEnd.isBefore(now)) {
            return "Expired";
        } else if (daysLeft <= 7) {
            return "Expiring Soon";
        } else {
            return "Active";
        }
    };

    // Ensure customers is always an array
    const customersArray = Array.isArray(customers) ? customers : [];

    // Apply client-side filters (for filters that backend doesn't support)
    const filteredCustomers = useMemo(() => {
        if (!customersArray || customersArray.length === 0) return [];
        
        return customersArray.filter(customer => {
            // Apply filterStatus filter
            if (filterStatus === "active") {
                if (customer.customer?.isActive !== true) return false;
            } else if (filterStatus === "inactive") {
                if (customer.customer?.isActive !== false) return false;
            }
            
            // Apply subscription filter
            if (subscriptionFilter !== 'all') {
                const subscriptionType = customer.subscription?.subscriptionType?.typeName || 'N/A';
                if (subscriptionType !== subscriptionFilter) return false;
            }
            
            // Apply trial status filter
            if (trialStatusFilter !== 'all') {
                const trialExpirationStatus = getTrialExpirationStatus(customer.subscription?.endDate);
                if (trialExpirationStatus !== trialStatusFilter) return false;
            }
            
            return true;
        });
    }, [customersArray, filterStatus, subscriptionFilter, trialStatusFilter]);

    // Column definitions for AG Grid
    const columns = useMemo(() => [
        { key: 'company', header: 'Company', width: 240, render: (row) => (
            <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate" title={row?.customer?.companyName}>
                    {row?.customer?.companyName || '—'}
                </p>
                <p className="text-xs text-slate-500 truncate" title={row?.customer?.emailId}>
                    {row?.customer?.emailId || '—'}
                </p>
            </div>
        )},
        { key: 'owner', header: 'Owner', width: 200, render: (row) => (
            <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate" title={row?.customer?.ownerName}>
                    {row?.customer?.ownerName || '—'}
                </p>
                <p className="text-xs text-slate-500 tabular-nums">
                    {row?.customer?.mobileNoWa || '—'}
                </p>
            </div>
        )},
        { key: 'subscription', header: 'Subscription', width: 160, render: (row) => (
            <SubscriptionTagRenderer value={row?.subscription?.subscriptionType?.typeName || 'N/A'} />
        )},
        { key: 'trial', header: 'Trial', width: 150, render: (row) => (
            <TrialStatusRenderer value={row?.subscription?.endDate} />
        )},
        { key: 'status', header: 'Status', width: 130, render: (row) => (
            <StatusRenderer value={row?.customer?.isActive || false} />
        )},
        { key: 'actions', header: 'Actions', width: 130, align: 'center', render: (row) => (
            <ActionsRenderer
                data={row}
                onActivateDeactivate={handleActivateDeactivateConfirmation}
                isLoading={isLoading}
            />
        )},
    ], [isLoading, handleActivateDeactivateConfirmation]);

    // Typing only updates local state; the debounced effect commits it automatically.
    const handleSearchChange = (query) => {
        setSearchQuery(query);
    };

    // Enter / search-button forces an immediate search (bypasses the debounce).
    const handleSearch = () => {
        changeSearch(searchQuery);
    };

    const { userInfo, setUserInfo } = useUser();

    const logout = () => {
        setUserInfo(null);
        sessionStorage.removeItem("userInfo");
        sessionStorage.removeItem("authToken");
        navigate("/");
    };

    return (
        <div className="p-4 md:p-6 bg-slate-50 min-h-screen">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Developers</h1>
                    <p className="text-sm text-slate-500 mt-0.5">
                        {pagination?.total ? `${pagination.total} developer account(s)` : 'Onboarded developer accounts and their subscriptions'}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => { setcustomerToEdit(null); setIsModalOpen(true); }}
                    className="inline-flex items-center gap-2 rounded-xl bg-flamingo-600 hover:bg-flamingo-700 text-white text-sm font-semibold px-5 py-2.5 shadow-sm hover:shadow transition-colors"
                >
                    <Plus size={16} /> Add Developer
                </button>
            </div>

            {/* Toolbar */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-4 flex flex-col sm:flex-row gap-3 sm:items-center">
                <div className="sm:w-1/3">
                    <SearchInput
                        value={searchQuery}
                        onChange={handleSearchChange}
                        onSearch={handleSearch}
                        placeholder="Search developers..."
                    />
                </div>

                {/* Plan filter — fixed plan list so it always shows regardless of the current page */}
                <select
                    value={subscriptionFilter}
                    onChange={(e) => setSubscriptionFilter(e.target.value)}
                    className="sm:w-56 rounded-xl border border-slate-300 bg-white py-2.5 px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-flamingo-500/40 focus:border-flamingo-500"
                >
                    <option value="all">All Plans</option>
                    <option value="BUILDVIZER_UNIFIED">BuildVizer Unified</option>
                    <option value="BUILDVIZER_PLUS">BuildVizer Plus</option>
                </select>
            </div>

            <DataTable
                columns={columns}
                data={filteredCustomers}
                loading={isLoading}
                minWidth={1010}
                rowKey={(r, i) => r?.customer?.customerId ?? i}
                emptyState={
                    <div className="px-6 py-12 text-center">
                        <p className="text-base font-semibold text-slate-900">
                            {searchQuery ? 'No customers match your search' : 'No customers yet'}
                        </p>
                        <p className="text-sm text-slate-500 mt-1">
                            {searchQuery
                                ? 'Try adjusting your search terms.'
                                : 'Get started by adding your first customer.'}
                        </p>
                    </div>
                }
            />

            {/* Pagination Controls */}
            {!isLoading && pagination.total > 0 && (
                <PaginationControls
                    pagination={pagination}
                    onPageChange={goToPage}
                    onPageSizeChange={changePageSize}
                    itemName="customers"
                    variant="list"
                    showInfo={true}
                />
            )}

            <CustomerModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setcustomerToEdit(null);
                }}
                onSubmit={handleSubmit}
                existingCustomer={customerToEdit?.customer}
                onFetchCustomerList={refetch}
            />

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={confirmationModalOpen}
                onConfirm={handleConfirmActivateDeactivate}
                onCancel={handleCancelActivateDeactivate}
                message={`Are you sure you want to ${actionType} this customer?`}
            />
        </div>
    );
};

export default CustomerList;